import { omitBy } from 'lodash';
import { Mutex } from 'async-mutex';

// Base RT Query service to query Votez backend
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';

import { RootState } from '@store/store';

import { logoff, updateToken } from '@store/accountSlice';

const baseUrl = '/api';

// Mutex to prevent concurrent refresh token requests
const mutex = new Mutex();

// Strip out all values with empty strings
function isEmptyValues(value?: unknown) {
  return (
    value === undefined ||
    value === null ||
    Number.isNaN(value) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
}


export const whitespacesAsNull = <T extends object>(values: T) => omitBy(values, isEmptyValues) as T;

// Prepare the base query and add security token if available
const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: 'include', // Send cookies with requests (for refresh token)

  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).account.user?.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

interface RefreshResponse {
  token: string;
}

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // Wait if another request is currently refreshing the token
  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  // Only attempt refresh if user was logged in (has a token)
  // For unauthenticated requests (like login with wrong password), don't retry
  const state = api.getState() as RootState;
  const hasToken = !!state.account.user?.token;

  if (result.error?.status === 401 && hasToken) {
    // Check if another request is already refreshing
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // Try to refresh the token
        const refreshResult = await baseQuery(
          { url: '/security/refresh', method: 'POST' },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          // Store the new access token
          const data = refreshResult.data as RefreshResponse;
          api.dispatch(updateToken(data.token));
          // Retry the original request with the new token
          result = await baseQuery(args, api, extraOptions);
        } else {
          // Refresh failed, log out the user
          api.dispatch(logoff());
        }
      } finally {
        release();
      }
    } else {
      // Another request is already refreshing, wait for it to complete
      await mutex.waitForUnlock();
      // Retry the original request (token should be updated now)
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const TagTypes = [
  'Adhesions',
  'ApplicationModules',
  'Clients',
  'ClientThemes',
  'ConfigClient',
  'Demissions',
  'Doublons',
  'Employes',
  'EmployesSignature',
  'Employeurs',
  'FeatureFlags',
  'ObjetConsentement',
  'SyndicatRivals',
  'MaraudagesParEmployeur',
  'ProprieteMembrePerClient',
  'Utilisateurs',
  'WalletTemplate',
  'ChampUtilisateur',
  'ChoixChampUtilisateur',
  'ConfigNouveauMembre',
  'Formulaires',
  'DemandeAdhesion',
  'DemandeAdhesionIndex',
  'Environnement',
  'SysUtilisateurs',
  'Campagnes',
  'VoteByCode',
  'Payments',
  'PaymentConfiguration',
  'EspacePublic',
  'CandidatPublic',
  'SysAdminDashboard',
];

const adminApi = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: TagTypes,
  endpoints: () => ({}),
  // Refetch when user returns to tab (catches updates made elsewhere)
  refetchOnFocus: true,
  // Don't refetch on every mount - rely on cache invalidation via invalidatesTags
  // Setting to true causes excessive API calls and UI flickering
  refetchOnMountOrArgChange: false,
  // Refetch when network connection is restored
  refetchOnReconnect: true,
});

export default adminApi;
